import XDate from 'xdate';

export class EventGuessingGamesStore {
  // The go-to place for guessing games-related things: what game(s)
  // can be added to an event based on the event type, what game
  // instance(s) exist on the event already, etc. Plus methods
  // for adding/removing/modifying guessing game instances.
  //
  // Note: this is used in both the create flow (for host-facing
  // game configuration/selection) and in view invitation, for
  // both host- and guest-facing functionality (has the viewing
  // guest already submitted guesses for a particular game, etc.)
  constructor(
    event,
    guessingGameConfigData,
    availableGuessingGamesByEventType,
    existingGuessingGameInstances
  ) {
    this.event = event || {};

    // initialize some default data member values
    this._availableGameConfigurationsByGameKey = {};
    this._ggKeysByEventType = {};
    this._existingGamesByGameKey = {};
    this._removedGamesByGameKey = {};

    // initialize stuff
    this._initializeAvailableGuessingGameConfigurationsFrom(guessingGameConfigData);
    this._initializeGuessingGameKeysByEventType(availableGuessingGamesByEventType);
    this._initializeExistingGuessingGames(existingGuessingGameInstances);

    // validation callbacks - this sort of piggy-backs on the "external validators"
    // stuff w/ EventFormStore (see use of the validateGameInstances method, below)
    // when our validateGameInstances() method is called (by EventFormStore, using
    // the external validation mechanism), additional functions can be called to
    // respond to validation just having been done (to update UIs, etc.)
    this._validationCallbacks = [];
  }

  addValidationCallback(callbackFcn) {
    this._validationCallbacks.push(callbackFcn);
  }

  removeValidationCallback(callbackFcn) {
    const fcnIdx = this._validationCallbacks.indexOf(callbackFcn);
    if (fcnIdx !== -1) this._validationCallbacks.pop(fcnIdx);
  }

  // this method is added to EventFormStore's "external validators"
  validateGameInstances = () => {
    let anyErrors = false;
    this.getAllGameInstances().forEach((gameInstance) => {
      anyErrors = anyErrors || gameInstance.doValidation();
    });
    this._validationCallbacks.forEach((callbackFcn) => callbackFcn());
    return anyErrors;
  };

  // this and handleEventSubmissionResponse(), below, are used w/ EventFormStore's
  // "external provider" mechanism (in the create flow)
  addToEventSubmissionData = (submitData) => {
    submitData.event.guessing_games_data = this.getGuessingGamesConfigurationUpdateData();
  };

  handleEventSubmissionResponse = (responseData) => {
    if (responseData.existing_game_instances) {
      this.updateExistingGameInstancesWithServerData(responseData.existing_game_instances);
    }
  };

  getGuessingGamesConfigurationUpdateData() {
    if (!this.hasAnyGameInstances()) {
      return {}; // easy peasy
    }

    const guessingGamesData = {};
    Object.keys(this._existingGamesByGameKey).forEach((gameKey) => {
      const gameInstance = this._existingGamesByGameKey[gameKey];
      guessingGamesData[gameKey] = gameInstance.getInstanceConfiguration();
    });
    return guessingGamesData;
  }

  updateExistingGameInstancesWithServerData(existingGuessingGameInstances) {
    this._initializeExistingGuessingGames(existingGuessingGameInstances);
  }

  getAvailableGameConfiguationsForEvent(eventType) {
    const gameKeys = (this._ggKeysByEventType[eventType] || []).concat(
      Object.keys(this._existingGamesByGameKey)
    );
    if (!gameKeys) return [];
    const retConfigObjs = [];
    gameKeys.forEach((gameKey) => {
      const configObj = this.getGameConfigurationWithKey(gameKey);
      if (configObj) retConfigObjs.push(configObj);
    });
    return retConfigObjs;
  }

  getAvailableGameConfigurationsForEventType(eventType) {
    const gameKeys = (this._ggKeysByEventType[eventType] || []).concat(
      this._existingGamesByGameKey
    );
    if (!gameKeys || gameKeys.length === 0) return [];
    const retConfigObjs = [];
    gameKeys.forEach((gameKey) => {
      const configObj = this.getGameConfigurationWithKey(gameKey);
      if (configObj) retConfigObjs.push(configObj);
    });
    return retConfigObjs;
  }

  getGameConfigurationWithKey(gameKey) {
    // Note: this returns the configuration from the event's _instance_
    // of the game if there is one
    if (this.hasInstanceOfGame(gameKey)) {
      const gameInstance = this.getGameInstanceWithKey(gameKey);
      return gameInstance.getGameConfiguration();
    }
    return this._availableGameConfigurationsByGameKey[gameKey];
  }

  hasAnyGameInstances() {
    return Object.keys(this._existingGamesByGameKey).length > 0;
  }

  hasInstanceOfGame(gameKey) {
    return this.getGameInstanceWithKey(gameKey) != null;
  }

  getGameInstanceWithKey(gameKey) {
    return this._existingGamesByGameKey[gameKey] || null;
  }

  getAllGameInstances() {
    const gameInstances = [];
    Object.keys(this._existingGamesByGameKey).forEach((gameKey) => {
      gameInstances.push(this.getGameInstanceWithKey(gameKey));
    });
    return gameInstances;
  }

  getFirstGameInstance() {
    if (!this.hasAnyGameInstances()) return null;
    return this.getAllGameInstances()[0];
  }

  createGameInstance(gameKey) {
    const previouslyRemovedGameInstance = this._removedGamesByGameKey[gameKey];
    if (previouslyRemovedGameInstance) {
      this._existingGamesByGameKey[gameKey] = previouslyRemovedGameInstance;
      delete this._removedGamesByGameKey[gameKey];
      return previouslyRemovedGameInstance;
    }

    if (this.hasInstanceOfGame(gameKey)) return this.getGameInstanceWithKey(gameKey);

    const gameConfig = this.getGameConfigurationWithKey(gameKey);
    if (!gameConfig) return null; // TODO also error?

    const gameInstanceData = {
      game_key: gameKey,
      game_config: gameConfig,
      instance_data: {
        instance_config: {},
        guest_submissions: {},
        winners: [],
      },
    };
    const newGameInstance = new EventGuessingGameInstance(gameInstanceData);
    this._existingGamesByGameKey[gameKey] = newGameInstance;
    return newGameInstance;
  }

  removeGameInstance(gameKey) {
    if (!this.hasInstanceOfGame(gameKey)) return; // nothing to do
    const gameInstance = this.getGameInstanceWithKey(gameKey);
    this._removedGamesByGameKey[gameKey] = gameInstance;
    delete this._existingGamesByGameKey[gameKey];
  }

  setGameLevelInputValue(gameKey, gliKey, gliValue) {
    this.getGameInstanceWithKey(gameKey).setGameLevelInputValue(gliKey, gliValue);
  }

  setGameQuestionInclusion(gameKey, questionKey, included = true) {
    this.getGameInstanceWithKey(gameKey).setQuestionInclusion(questionKey, included);
  }

  setSendUpdatesOnGuessesFor(gameKey, sendUpdates = true) {
    this.getGameInstanceWithKey(gameKey).setSendUpdatesOnGuesses(sendUpdates);
  }

  getEventId() {
    return this.event.id || null;
  }

  hasRsvp() {
    return Boolean(this.event.rsvp && this.event.rsvp.date_replied);
  }

  getRsvp() {
    return this.hasRsvp() ? this.event.rsvp : {};
  }

  isHost() {
    return this.getRsvp().host === true;
  }

  hasGuestId() {
    return this.getGuestId() !== null;
  }

  getGuestId() {
    return this.getRsvp().id || null;
  }

  hasUserId() {
    // TODO tests for this
    return this.getUserId() !== null;
  }

  getUserId() {
    // TODO tests for this
    return this.getRsvp().user_id || null;
  }

  isEventOver() {
    return window.event_has_passed;
  }

  getIncludedQuestionKeysForGame(gameKey) {
    const gameInstance = this.getGameInstanceWithKey(gameKey);
    if (!gameInstance) return [];
    return gameInstance.getIncludedQuestionKeys();
  }

  getQuestionDataFor(gameKey, questionKey) {
    const gameInstance = this.getGameInstanceWithKey(gameKey);
    if (!gameInstance) return null;
    return gameInstance.getQuestionDataFor(questionKey);
  }

  hasGuestSubmittedForGame(gameKey) {
    return this.getGuestQuestionResponsesForGame(gameKey, false) !== null;
  }

  hasGuestSubmittedForAnyGames() {
    let hasSubmitted = false;
    this.getAllGameInstances().forEach((gameInstance) => {
      if (this.hasGuestSubmittedForGame(gameInstance.gameKey)) {
        hasSubmitted = true;
      }
    });
    return hasSubmitted;
  }

  getGuestQuestionResponsesForGame(gameKey, initWithDefaultValues = true) {
    const gameInstance = this.getGameInstanceWithKey(gameKey);
    if (!gameInstance) return null;

    const userId = this.getUserId() || 'no_user_id';
    let submissionData = gameInstance.getInstanceOnlyData().guest_submissions[userId];
    if (!submissionData) {
      submissionData = initWithDefaultValues ? gameInstance.getDefaultGuestResponseData() : null;
    }
    return submissionData;
  }

  submitGuestResponseDataFor = async (gameKey, guestResponseData, submissionCallback = null) => {
    // TODO tests for this
    const submitUrl = `/ajax/event/${this.getEventId()}/guessing-games/submit/`;
    const postData = {
      game_key: gameKey,
      guest_id: this.getGuestId(),
      submission_data: guestResponseData,
    };
    let isError = false;
    const response = await evite.fetch(submitUrl, {
      method: 'POST',
      body: JSON.stringify(postData),
    });
    if (!response.ok) {
      isError = true;
    }
    const responseData = await response.json();
    if (isError) {
      // error (probably a validation error)
      if (submissionCallback) {
        submissionCallback(false, responseData.message, guestResponseData);
      } else {
        alert(responseData.message);
      }
    } else {
      // not an error, so save responseData.submission_data as the user's
      // submission for this event/game
      this.updateGuestResponseDataFor(gameKey, responseData.submission_data);
      this.clearGameSummaryFor(gameKey);
      if (submissionCallback) {
        submissionCallback(true, responseData.message, responseData.submission_data);
      } else {
        alert('Submission accepted.');
      }
    }
  };

  updateGuestResponseDataFor(gameKey, guestResponseData) {
    if (!this.hasGuestId()) return;
    const gameInstance = this.getGameInstanceWithKey(gameKey);
    if (!gameInstance) return;

    gameInstance.setGuestResponses(this.getGuestId(), guestResponseData);
  }

  getDefaultHostAnswerDataForGame(gameKey) {
    // TODO test for this
    const gameInstance = this.getGameInstanceWithKey(gameKey);
    if (!gameInstance) return {};

    return gameInstance.getDefaultHostAnswerData();
  }

  submitAnswersFromHostFor = async (gameKey, hostAnswersData, submissionCallback = null) => {
    // TODO tests for this
    const submitUrl = `/ajax/event/${this.getEventId()}/guessing-games/submit-answers/`;
    const postData = {
      game_key: gameKey,
      user_id: this.getUserId(),
      answer_data: hostAnswersData,
    };
    let isError = false;
    const response = await evite.fetch(submitUrl, {
      method: 'POST',
      body: JSON.stringify(postData),
    });
    if (!response.ok) {
      isError = true;
    }
    const responseData = await response.json();
    if (isError) {
      // error (probably a validation error)
      if (submissionCallback) {
        submissionCallback(false, responseData.message, hostAnswersData);
      } else {
        alert(responseData.message);
      }
    } else {
      // not an error, so save responseData.answers_data as the user's
      // submission for this event/game
      this.updateGameInstanceDataFor(gameKey, responseData.game_instance_data);
      this.clearGameSummaryFor(gameKey);
      if (submissionCallback) {
        submissionCallback(
          true,
          responseData.message,
          responseData.game_instance_data // ?? TODO maybe something else?
        );
      } else {
        alert('Answers accepted.');
      }
    }
  };

  clearGameSummaryFor(gameKey) {
    // clear game summary data such that it will be re-fetched
    // this should be called after a guest submits their answers or
    // after a host submits the actual answers, so the summary
    // view will get re-fetched when needed
    // TODO test for this
    const gameInstance = this.getGameInstanceWithKey(gameKey);
    gameInstance.clearGameSummary();
  }

  getGameSummaryFor(gameKey, afterFetchedCallback = null) {
    // TODO test for this
    if (this.hasGameSummaryFor(gameKey)) {
      const gameInstance = this.getGameInstanceWithKey(gameKey);
      return gameInstance.getGameSummary();
    }

    this.fetchGameSummaryFor(gameKey, afterFetchedCallback);
    return null;
  }

  hasGameSummaryFor(gameKey) {
    // TODO test for this
    const gameInstance = this.getGameInstanceWithKey(gameKey);
    return gameInstance.hasGameSummary();
  }

  addGameSummaryFor(gameKey, summaryData) {
    // TODO test for this
    const gameInstance = this.getGameInstanceWithKey(gameKey);
    return gameInstance.addGameSummaryWith(summaryData);
  }

  async fetchGameSummaryFor(gameKey, afterFetchedCallback = null) {
    // TODO tests for this
    const eid = this.getEventId();
    const uid = this.getUserId();
    const uidParam = this.hasUserId() ? `?user_id=${this.getUserId()}` : '';
    const fetchSummaryUrl = `/ajax/event/${eid}/guessing-games/${gameKey}/full-summary/${uidParam}`;
    let isError = false;
    const response = await evite.fetch(fetchSummaryUrl, {
      method: 'GET',
    });
    if (!response.ok) {
      isError = true;
    }
    const responseData = await response.json();
    if (isError) {
      // error (probably a validation error)
      if (afterFetchedCallback) {
        afterFetchedCallback(false, responseData.message, null);
      } else {
        alert(responseData.message);
      }
    } else {
      // not an error
      // update the game instance for this game
      this.updateGameInstanceDataFor(gameKey, responseData.instance_data);
      // TODO update game config w/ responseData.game_config eh?
      // also create/update the game summary for this game from responseData.summary_data
      const gameSummaryObj = this.addGameSummaryFor(gameKey, responseData.summary_data);
      if (afterFetchedCallback) {
        afterFetchedCallback(true, 'Success', gameSummaryObj);
      } else {
        alert('Summary data fetched.');
      }
    }
  }

  updateGameInstanceDataFor(gameKey, gameInstanceData) {
    // TODO test for this
    const gameInstance = this.getGameInstanceWithKey(gameKey);
    if (!gameInstance) return;

    gameInstance.updateInstanceOnlyDataWith(gameInstanceData);
  }

  //
  // initialization methods...
  //

  _initializeAvailableGuessingGameConfigurationsFrom(guessingGameConfigData) {
    // guessingGameConfigData is an object containing game configuration objects,
    // keyed by "game key"
    this._availableGameConfigurationsByGameKey = {};
    if (!guessingGameConfigData) return; // that was easy

    Object.keys(guessingGameConfigData).forEach((gameKey) => {
      const gameConfigData = guessingGameConfigData[gameKey];
      const gameConfigObj = new GuessingGameConfiguration(gameConfigData);
      this._availableGameConfigurationsByGameKey[gameKey] = gameConfigObj;
    });
  }

  _initializeGuessingGameKeysByEventType(availableGuessingGamesByEventType) {
    this._ggKeysByEventType = {};
    if (availableGuessingGamesByEventType) {
      this._ggKeysByEventType = availableGuessingGamesByEventType;
      // TODO anything else?
    }
  }

  _initializeExistingGuessingGames(existingGuessingGameInstances) {
    // existingGuessingGameInstances should be an array of objects, where
    // each object has the following:
    // - game_key: the game key (string)
    // - game_config: the game configuration for this instance of the game
    // - instance_data: the instance-specific stuff only, including an
    //   "instance_config" object (with a "game_level_inputs" object, a
    //   "questions" object, "send_updates_on_guesses" flag, etc.), a
    //   "guest_submissions" object (guest's guesses keyed by their user IDs),
    //   and a "winners" array (user IDs of winners, once declared)
    this._existingGamesByGameKey = {};
    if (!existingGuessingGameInstances) return; // so easy

    existingGuessingGameInstances.forEach((gameInstanceData) => {
      const gameKey = gameInstanceData.game_key;
      this._existingGamesByGameKey[gameKey] = new EventGuessingGameInstance(gameInstanceData);
    });
  }
}

//
// classes for guessing game things (used/instantiated by EventGuessingGamesStore)
//

export class EventGuessingGameInstance {
  // Encapsulation of the data needed for an instance of a guessing game
  // on an event: the game key, the up-to-date game configuration, and
  // the instance-only data (sparse changes that get applied to a game
  // configuration).
  constructor(gameInstanceData) {
    this.gameKey = gameInstanceData.game_key;
    this.gameConfig = new GuessingGameConfiguration(gameInstanceData.game_config);
    this.gameInstanceOnlyData = new EventGuessingGameInstanceOnlyData(
      gameInstanceData.instance_data
    );
    this.clearGameSummary();
  }

  doValidation() {
    return this.getGameConfiguration().doValidation();
  }

  getGameConfiguration() {
    return this.gameConfig;
  }

  getInstanceOnlyData() {
    return this.gameInstanceOnlyData;
  }

  getInstanceConfiguration() {
    return this.getInstanceOnlyData().getInstanceConfiguration();
  }

  hasGameSummary() {
    // TODO test for this
    return this.getGameSummary() !== null;
  }

  getGameSummary() {
    // TODO test for this
    return this.gameSummary;
  }

  addGameSummaryWith(gameSummaryData) {
    // TODO test for this
    this.gameSummary = new EventGuessingGameSummary(this.gameKey, gameSummaryData);
    return this.gameSummary;
  }

  clearGameSummary() {
    // TODO test for this
    this.gameSummary = null;
  }

  hasAnyGuestSubmissions() {
    if (this.hasGameSummary()) {
      return this.getGameSummary().guest_plays.length > 0;
    }
    return this.getInstanceOnlyData().hasAnyGuestSubmissions();
  }

  doesHostEnterAnswers() {
    return this.gameConfig.doesHostEnterAnswers();
  }

  getDefaultGuestResponseData() {
    return this._getObjWithIncludedQuestionExpectedValues();
  }

  getDefaultHostAnswerData() {
    return this._getObjWithIncludedQuestionExpectedValues();
  }

  getQuestionWithKey(questionKey) {
    return this.gameConfig.getQuestionWithKey(questionKey);
  }

  getIncludedQuestionKeys() {
    const questionKeys = [];
    this.getGameConfiguration().questions.forEach((questionObj) => {
      if (questionObj.included) questionKeys.push(questionObj.key);
    });
    return questionKeys;
  }

  getIncludedQuestionsByKey() {
    const inclQuestionKeys = this.getIncludedQuestionKeys();
    const questionsByKey = {};
    inclQuestionKeys.forEach((questionKey) => {
      questionsByKey[questionKey] = this.getQuestionWithKey(questionKey);
    });
    return questionsByKey;
  }

  getIncludedQuestions() {
    const inclQuestionKeys = this.getIncludedQuestionKeys();
    const questionsArray = [];
    inclQuestionKeys.forEach((questionKey) => {
      questionsArray.push(this.getQuestionWithKey(questionKey));
    });
    return questionsArray;
  }

  getIncludedQuestionsOfType(questionType) {
    const inclQuestions = this.getIncludedQuestions();
    return inclQuestions.filter((questionObj) => questionObj.type === questionType);
  }

  hasIncludedWeightGuessQuestion() {
    return this.hasIncludedQuestionOfType('weight_guess');
  }

  hasIncludedLengthGuessQuestion() {
    return this.hasIncludedQuestionOfType('length_guess');
  }

  hasIncludedQuestionOfType(questionTypeStr) {
    return this.getIncludedQuestionsOfType(questionTypeStr).length > 0;
  }

  getQuestionDataFor(questionKey) {
    return this.getGameConfiguration().getQuestionWithKey(questionKey);
  }

  isGameActive() {
    return this.gameInstanceOnlyData.isGameActive();
  }

  isGameOver() {
    return !this.isGameActive();
  }

  setGameLevelInputValue(gliKey, gliValue) {
    this.gameConfig.setGameLevelInputValue(gliKey, gliValue);
    this.gameInstanceOnlyData.setGameLevelInputValue(gliKey, gliValue);
  }

  setQuestionInclusion(questionKey, included = true) {
    this.gameConfig.setQuestionInclusion(questionKey, included);
    this.gameInstanceOnlyData.setQuestionInclusion(questionKey, included);
  }

  setSendUpdatesOnGuesses(sendUpdates) {
    this.gameConfig.setSendUpdatesOnGuesses(sendUpdates);
    this.gameInstanceOnlyData.setSendUpdatesOnGuesses(sendUpdates);
  }

  setGuestResponses(guestId, guestResponseData) {
    this.gameInstanceOnlyData.setGuestResponses(guestId, guestResponseData);
  }

  updateInstanceOnlyDataWith(gameInstanceData) {
    // gameInstanceData is basically a JSON-serialized EventGuessingGameInstanceModel
    // instance (from backend), so we should do two things:
    // 1. update this.gameConfig to have gameInstanceData's instance_config applied
    // 2. replace this.gameInstanceOnlyData with a new one, based on gameInstanceData
    this.gameConfig.applyInstanceConfiguration(gameInstanceData.instance_config);
    this.gameInstanceOnlyData = new EventGuessingGameInstanceOnlyData(gameInstanceData);
  }

  _getObjWithIncludedQuestionExpectedValues() {
    const retObj = {};
    this.getIncludedQuestions().forEach((questionObj) => {
      if (questionObj.hasExpectedValue()) {
        retObj[questionObj.key] = questionObj.getExpectedValue();
      }
    });
    return retObj;
  }
}

export class GuessingGameConfiguration {
  // Representation of a game configuration, including arrays of game-level
  // inputs and questions, sponsor-related data, etc.
  constructor(gameConfigData) {
    // first set some defaults
    this.game_level_inputs = [];
    this.glisByKey = {};
    this.questions = [];
    this.questionsByKey = {};

    // then initialize with what's incoming
    Object.keys(gameConfigData).forEach((attrName) => {
      this[attrName] = gameConfigData[attrName];
    });

    // initialize objects for game-level inputs
    this.game_level_inputs = this.game_level_inputs.map(
      (gliData) => new GuessingGameGameLevelInput(gliData)
    );
    // initialize this.glisByKey
    this.game_level_inputs.forEach((gliObj) => {
      this.glisByKey[gliObj.key] = gliObj;
    });

    // initialize objects for questions
    this.questions = this.questions.map((questionData) => new GuessingGameQuestion(questionData));
    // ...and initialize this.questionsByKey
    this.questions.forEach((questionObj) => {
      this.questionsByKey[questionObj.key] = questionObj;
    });

    // default validation error
    this.gliValidationErrors = {};
  }

  getGameLevelInputWithKey(gliKey) {
    return this.glisByKey[gliKey] || null;
  }

  getQuestionKeys() {
    return Object.keys(this.questionsByKey);
  }

  getQuestionWithKey(questionKey) {
    return this.questionsByKey[questionKey] || null;
  }

  setGameLevelInputValue(gliKey, gliValue) {
    const gliObj = this.getGameLevelInputWithKey(gliKey);
    if (!gliObj) return;
    gliObj.value = gliValue;
  }

  setQuestionInclusion(questionKey, included = true) {
    const questionObj = this.getQuestionWithKey(questionKey);
    if (!questionObj) return;
    questionObj.included = included;
  }

  setSendUpdatesOnGuesses(sendUpdates) {
    this.send_updates_on_guesses = Boolean(sendUpdates);
  }

  doesHostEnterAnswers() {
    return this.answers_source === 'host_enters';
  }

  hasSponsor() {
    return Boolean(this.sponsor_data);
  }

  getSponsorName(convertToLowerCase = true) {
    if (!this.hasSponsor() || !this.sponsor_data.name) return '';
    const sponsorName = this.sponsor_data.name;
    return convertToLowerCase ? sponsorName.toLowerCase() : sponsorName;
  }

  getSponsorFontClassString() {
    const sponsorName = this.getSponsorName();
    return sponsorName ? `${sponsorName}-font` : '';
  }

  getSponsorInfoText() {
    if (!this.hasSponsor()) return '';
    return this.sponsor_data.info_text || '';
  }

  hasSponsorLogoImageUrl() {
    return Boolean(this.getSponsorLogoImageUrl());
  }

  getSponsorLogoImageUrl() {
    if (!this.hasSponsor()) return '';
    return this.sponsor_data.logo_image_url || '';
  }

  getGuestSubmissionDescriptionText(defaultMessage = 'guest submission') {
    // TODO: It is likely that which label will be specified somehow.
    // Just using medium/singular for now.
    if (
      this.notifications &&
      this.notifications.submission_label &&
      this.notifications.submission_label.medium &&
      this.notifications.submission_label.medium.singular
    ) {
      return this.notifications.submission_label.medium.singular;
    }
    return defaultMessage;
  }

  applyInstanceConfiguration(instanceConfig) {
    // the only thing in instanceConfig we really need to
    // look at/update is "questions"
    const latestQuestionsConfigData = instanceConfig.questions || {};
    Object.keys(latestQuestionsConfigData).forEach((questionKey) => {
      const newQuestionData = latestQuestionsConfigData[questionKey];
      const questionObj = this.getQuestionWithKey(questionKey);
      Object.keys(newQuestionData).forEach((propName) => {
        questionObj[propName] = newQuestionData[propName];
      });
    });
  }

  //
  // validation stuff - this is used from the create flow to check
  // (and enable host to correct) any host-entered values before trying
  // to submit changes to backend
  //

  doValidation() {
    this.validateGameLevelInputs();
    return this.hasAnyValidationErrors();
  }

  hasAnyValidationErrors() {
    return this.hasAnyGameLevelInputValidationErrors();
  }

  validateGameLevelInputs() {
    this.gliValidationErrors = {};
    this.game_level_inputs.forEach((gliObj) => {
      const validationErrorStr = gliObj.validate();
      if (validationErrorStr) this.gliValidationErrors[gliObj.key] = validationErrorStr;
    });
  }

  hasAnyGameLevelInputValidationErrors() {
    return Object.keys(this.getGameLevelInputValidationErrors()).length > 0;
  }

  getGameLevelInputValidationErrors() {
    return this.gliValidationErrors;
  }
}

export class GuessingGameGameLevelInput {
  // TODO tests for this puppy
  constructor(gliData) {
    Object.keys(gliData).forEach((attrName) => {
      this[attrName] = gliData[attrName];
    });
  }

  isRequired() {
    return this.required;
  }

  hasValue() {
    return this.value !== null;
  }

  validate() {
    // return a validation error string if there's a problem, otherwise
    // an empty string
    if (this.isRequired() && !this.hasValue()) {
      return `${this.prompt} is required`;
    }
    return '';
  }
}

export class GuessingGameQuestion {
  // TODO tests for this puppy
  constructor(questionConfigData) {
    Object.keys(questionConfigData).forEach((attrName) => {
      this[attrName] = questionConfigData[attrName];
    });
  }

  hasExpectedValue() {
    return this.expected_value !== null;
  }

  getExpectedValue() {
    return this.expected_value;
  }

  hasAnswer() {
    return this.answer !== undefined;
  }

  getAnswerValue() {
    return this.answer;
  }

  getAnswerValueAsString() {
    return this.getSubmittedValueAsString(this.getAnswerValue());
  }

  getSubmittedValueAsString(submittedValue) {
    if (submittedValue === undefined) return '';

    switch (this.type) {
      case 'date_time_guess':
        return _getDescriptiveDateStringFor(submittedValue);
      case 'binary_choice':
        return submittedValue;
      case 'multiple_choice':
        return submittedValue;
      case 'weight_guess':
        return `${submittedValue.pounds} lbs, ${submittedValue.ounces} oz`;
      case 'length_guess':
        return `${submittedValue} in`;
      // more as/if needed
    }
    return '(unknown question type)';
  }
}

export class EventGuessingGameInstanceOnlyData {
  // Representation of the "instance only" data for a guessing game present
  // on an event. Contains things like instance_config (sparse configuration
  // changes for this instance/event), scoring_data (game scores keyed by
  // guests' user IDs), guest_submissions (guests' question submissions keyed
  // by their user IDs), and winners (array of guests' user IDs - winners of
  // the game).
  constructor(instanceOnlyData) {
    // first set some defaults
    this.game_key = '';
    this.submissions_accepted = true;
    this.instance_config = {};
    this.scoring_data = {};
    this.guest_submissions = {};
    this.winners = [];

    // then initialize with what's incoming
    Object.keys(instanceOnlyData).forEach((attrName) => {
      this[attrName] = instanceOnlyData[attrName];
    });
  }

  getInstanceConfiguration() {
    return this.instance_config;
  }

  isGameActive() {
    return this.submissions_accepted === true;
  }

  hasAnyGuestSubmissions() {
    return Object.keys(this.guest_submissions).length > 0;
  }

  getGuestScore(guestUserId) {
    return this.scoring_data[guestUserId] || 0.0;
  }

  setGameLevelInputValue(gliKey, gliValue) {
    if (!this.instance_config.game_level_inputs) this.instance_config.game_level_inputs = {};

    if (!this.instance_config.game_level_inputs[gliKey])
      this.instance_config.game_level_inputs[gliKey] = {};

    this.instance_config.game_level_inputs[gliKey].value = gliValue;
  }

  setQuestionInclusion(questionKey, included = true) {
    if (!this.instance_config.questions) this.instance_config.questions = {};

    if (!this.instance_config.questions[questionKey])
      this.instance_config.questions[questionKey] = {};

    this.instance_config.questions[questionKey].included = Boolean(included);
  }

  setSendUpdatesOnGuesses(sendUpdates) {
    this.instance_config.send_updates_on_guesses = Boolean(sendUpdates);
  }

  setGuestResponses(guestId, guestResponseData) {
    this.guest_submissions[guestId] = guestResponseData;
  }
}

export class EventGuessingGameSummary {
  // TODO say what this is
  // TODO tests for these methods, etc.
  constructor(gameKey, guessingGameSummaryData) {
    this.gameKey = gameKey;

    // then initialize with what's incoming
    Object.keys(guessingGameSummaryData).forEach((attrName) => {
      this[attrName] = guessingGameSummaryData[attrName];
    });
    // note: should now have the following properties:
    // game_is_over (boolean)
    // guest_names_by_user_id (Object)
    // guest_plays (Array of Object - see doc string of get_full_game_summary() for info)
    // question_summaries (Object - see doc string of get_full_game_summary() for info)
    // winning_guest_names (Array of strings)
  }

  getNameOfGuestWithUserID(userId) {
    return this.guest_names_by_user_id[userId] || 'Unknown Guest';
  }
}

//
// helper functions
//

export const _getDescriptiveDateStringFor = (dtValueStr) => {
  const xDateTime = new XDate(dtValueStr);
  return `${xDateTime.toString('M/d/yy')} at ${xDateTime.toString('h:mmtt')}`;
};
